/**
 * useIsNarrowViewport
 * Layer: shared/hooks (no external deps beyond React)
 *
 * Tracks whether the viewport is at or below `breakpointPx` via
 * window.matchMedia, so components can conditionally simplify their layout
 * on narrow (phone-width) screens without pulling in a CSS/@media pattern.
 *
 * Falls back to `false` (treated as wide/desktop) when window.matchMedia is
 * unavailable — this app's jsdom-based unit tests do not polyfill it by
 * default, so this keeps existing unmocked tests rendering the desktop
 * layout instead of throwing.
 */
import { useState, useEffect } from 'react';

function matchesBreakpoint(breakpointPx: number): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
}

export function useIsNarrowViewport(breakpointPx = 480): boolean {
  const [isNarrow, setIsNarrow] = useState(() => matchesBreakpoint(breakpointPx));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mql = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    setIsNarrow(mql.matches);

    const onChange = (e: MediaQueryListEvent) => { setIsNarrow(e.matches); };
    mql.addEventListener('change', onChange);
    return () => { mql.removeEventListener('change', onChange); };
  }, [breakpointPx]);

  return isNarrow;
}
